import { lazy, Suspense, ReactElement } from 'react';
import { Box, CircularProgress } from '@mui/material';

/**
 * Page loader component for lazy-loaded routes
 * Displays a centered spinner while the page is loading
 */
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '400px',
      width: '100%',
    }}
  >
    <CircularProgress />
  </Box>
);

/**
 * HOC to wrap lazy components with Suspense
 * Returns a component function that renders the lazy component with Suspense
 */
export function withSuspense(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  LazyComponent: React.LazyExoticComponent<React.ComponentType<any>>,
): () => ReactElement {
  return function WrappedComponent() {
    return (
      <Suspense fallback={<PageLoader />}>
        <LazyComponent />
      </Suspense>
    );
  };
}

// =========================================
// Traffic Manager Pages
// =========================================

export const LazyTMDashboardPage = lazy(() =>
  import('../pages/traffic-manager/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  })),
);

export const LazyContractorsPage = lazy(() =>
  import('../pages/traffic-manager/ContractorsPage').then((module) => ({
    default: module.ContractorsPage,
  })),
);

export const LazyContractorViewPage = lazy(() =>
  import('../pages/traffic-manager/ContractorViewPage').then((module) => ({
    default: module.ContractorViewPage,
  })),
);

export const LazyTMContractorForecastsPage = lazy(() =>
  import('../pages/traffic-manager/ContractorForecastsPage').then((module) => ({
    default: module.ContractorForecastsPage,
  })),
);

export const LazyTMBranchViewPage = lazy(() =>
  import('../pages/traffic-manager/TMBranchViewPage').then((module) => ({
    default: module.TMBranchViewPage,
  })),
);

export const LazyTMPeriodCrossAnalyticsPage = lazy(() =>
  import('../pages/traffic-manager/TMPeriodCrossAnalyticsPage').then(
    (module) => ({
      default: module.TMPeriodCrossAnalyticsPage,
    }),
  ),
);

export const LazyBranchesPage = lazy(() =>
  import('../pages/traffic-manager/BranchesPage').then((module) => ({
    default: module.BranchesPage,
  })),
);

export const LazyBranchDashboardViewPage = lazy(() =>
  import('../pages/traffic-manager/BranchDashboardViewPage').then((module) => ({
    default: module.BranchDashboardViewPage,
  })),
);

export const LazyPnlMonthViewPage = lazy(() =>
  import('../pages/traffic-manager/PnlMonthViewPage').then((module) => ({
    default: module.PnlMonthViewPage,
  })),
);

export const LazyPlansPage = lazy(() =>
  import('../pages/traffic-manager/PlansPage').then((module) => ({
    default: module.PlansPage,
  })),
);

export const LazyOrganicSourcesPage = lazy(() =>
  import('../pages/traffic-manager/OrganicSourcesPage').then((module) => ({
    default: module.OrganicSourcesPage,
  })),
);

export const LazyTrafficChannelsPage = lazy(() =>
  import('../pages/traffic-manager/TrafficChannelsPage').then((module) => ({
    default: module.TrafficChannelsPage,
  })),
);

export const LazyManagersPage = lazy(() =>
  import('../pages/traffic-manager/ManagersPage').then((module) => ({
    default: module.ManagersPage,
  })),
);

export const LazyTMPaymentsPage = lazy(() =>
  import('../pages/traffic-manager/PaymentsPage').then((module) => ({
    default: module.PaymentsPage,
  })),
);

export const LazyApiClientsPage = lazy(() =>
  import('../pages/traffic-manager/ApiClientsPage').then((module) => ({
    default: module.ApiClientsPage,
  })),
);

export const LazyApiCallLogsPage = lazy(() =>
  import('../pages/traffic-manager/ApiCallLogsPage').then((module) => ({
    default: module.ApiCallLogsPage,
  })),
);

export const LazySourcesAnalyticsPage = lazy(() =>
  import('../pages/traffic-manager/SourcesAnalyticsPage').then((module) => ({
    default: module.SourcesAnalyticsPage,
  })),
);

export const LazyIntegrationsPage = lazy(() =>
  import('../pages/traffic-manager/IntegrationsPage').then((module) => ({
    default: module.IntegrationsPage,
  })),
);

export const LazyLeadAttributesPage = lazy(() =>
  import('../pages/traffic-manager/LeadAttributesPage').then((module) => ({
    default: module.LeadAttributesPage,
  })),
);

// =========================================
// Contractor Pages
// =========================================

export const LazyContractorDashboardPage = lazy(() =>
  import('../pages/contractor/DashboardPage').then((module) => ({
    default: module.ContractorDashboardPage,
  })),
);

export const LazyBranchCardPage = lazy(() =>
  import('../pages/contractor/BranchCardPage').then((module) => ({
    default: module.BranchCardPage,
  })),
);

export const LazyForecastPage = lazy(() =>
  import('../pages/contractor/ForecastPage').then((module) => ({
    default: module.ForecastPage,
  })),
);

export const LazyContractorPaymentsPage = lazy(() =>
  import('../pages/contractor/PaymentsPage').then((module) => ({
    default: module.ContractorPaymentsPage,
  })),
);

// =========================================
// Branch Pages
// =========================================

export const LazyBranchDashboardPage = lazy(() =>
  import('../pages/branch/DashboardPage').then((module) => ({
    default: module.BranchDashboardPage,
  })),
);

export const LazyPnlEditPage = lazy(() =>
  import('../pages/branch/PnlEditPage').then((module) => ({
    default: module.PnlEditPage,
  })),
);

export const LazyPlansApprovalPage = lazy(() =>
  import('../pages/branch/PlansApprovalPage').then((module) => ({
    default: module.PlansApprovalPage,
  })),
);

export const LazyBranchPaymentsPage = lazy(() =>
  import('../pages/branch/PaymentsPage').then((module) => ({
    default: module.BranchPaymentsPage,
  })),
);

// =========================================
// Shared Pages
// =========================================

export const LazyInboxPage = lazy(() =>
  import('../pages/shared/InboxPage').then((module) => ({
    default: module.InboxPage,
  })),
);

export const LazyMailingsPage = lazy(() =>
  import('../pages/traffic-manager/MailingsPage').then((module) => ({
    default: module.MailingsPage,
  })),
);

export const LazyAccountSettingsPage = lazy(() =>
  import('../pages/shared/AccountSettingsPage').then((module) => ({
    default: module.AccountSettingsPage,
  })),
);

export const LazyYandexAnalyticsPage = lazy(() =>
  import('../pages/shared/YandexAnalyticsPage').then((module) => ({
    default: module.YandexAnalyticsPage,
  })),
);

export const LazyPublicInvoicePage = lazy(() =>
  import('../pages/public/PublicInvoicePage').then((module) => ({
    default: module.PublicInvoicePage,
  })),
);

// =========================================
// Account Manager Pages
// =========================================

export const LazyAMDashboardPage = lazy(() =>
  import('../pages/account-manager/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  })),
);

export const LazyAMBranchViewPage = lazy(() =>
  import('../pages/account-manager/BranchViewPage').then((module) => ({
    default: module.BranchViewPage,
  })),
);

// =========================================
// Wrapped Components with Suspense
// =========================================

// Traffic Manager
export const TMDashboardPage = withSuspense(LazyTMDashboardPage);
export const ContractorsPage = withSuspense(LazyContractorsPage);
export const ContractorViewPage = withSuspense(LazyContractorViewPage);
export const TMContractorForecastsPage = withSuspense(
  LazyTMContractorForecastsPage,
);
export const TMBranchViewPage = withSuspense(LazyTMBranchViewPage);
export const TMPeriodCrossAnalyticsPage = withSuspense(
  LazyTMPeriodCrossAnalyticsPage,
);
export const BranchesPage = withSuspense(LazyBranchesPage);
export const BranchDashboardViewPage = withSuspense(
  LazyBranchDashboardViewPage,
);
export const PnlMonthViewPage = withSuspense(LazyPnlMonthViewPage);
export const PlansPage = withSuspense(LazyPlansPage);
export const OrganicSourcesPage = withSuspense(LazyOrganicSourcesPage);
export const TrafficChannelsPage = withSuspense(LazyTrafficChannelsPage);
export const ManagersPage = withSuspense(LazyManagersPage);
export const TMPaymentsPage = withSuspense(LazyTMPaymentsPage);
export const ApiClientsPage = withSuspense(LazyApiClientsPage);
export const ApiCallLogsPage = withSuspense(LazyApiCallLogsPage);
export const LeadAttributesPage = withSuspense(LazyLeadAttributesPage);
export const IntegrationsPage = withSuspense(LazyIntegrationsPage);
export const SourcesAnalyticsPage = withSuspense(LazySourcesAnalyticsPage);
export const MailingsPage = withSuspense(LazyMailingsPage);

// Contractor
export const ContractorDashboardPage = withSuspense(
  LazyContractorDashboardPage,
);
export const BranchCardPage = withSuspense(LazyBranchCardPage);
export const ForecastPage = withSuspense(LazyForecastPage);
export const ContractorPaymentsPage = withSuspense(LazyContractorPaymentsPage);

// Branch
export const BranchDashboardPage = withSuspense(LazyBranchDashboardPage);
export const PnlEditPage = withSuspense(LazyPnlEditPage);
export const PlansApprovalPage = withSuspense(LazyPlansApprovalPage);
export const BranchPaymentsPage = withSuspense(LazyBranchPaymentsPage);

// Account Manager
export const AMDashboardPage = withSuspense(LazyAMDashboardPage);
export const AMBranchViewPage = withSuspense(LazyAMBranchViewPage);

// Shared
export const InboxPage = withSuspense(LazyInboxPage);
export const AccountSettingsPage = withSuspense(LazyAccountSettingsPage);
export const YandexAnalyticsPage = withSuspense(LazyYandexAnalyticsPage);
export const PublicInvoicePage = withSuspense(LazyPublicInvoicePage);

// =========================================
// Prefetch helpers for navigation
// =========================================

/**
 * Prefetch route modules on hover/focus for better UX
 * Call these functions when user hovers over navigation links
 */
export const prefetchRoutes = {
  // Traffic Manager routes
  tmDashboard: () => import('../pages/traffic-manager/DashboardPage'),
  contractors: () => import('../pages/traffic-manager/ContractorsPage'),
  branches: () => import('../pages/traffic-manager/BranchesPage'),
  plans: () => import('../pages/traffic-manager/PlansPage'),
  tmPayments: () => import('../pages/traffic-manager/PaymentsPage'),
  mailings: () => import('../pages/traffic-manager/MailingsPage'),

  // Contractor routes
  contractorDashboard: () => import('../pages/contractor/DashboardPage'),
  forecast: () => import('../pages/contractor/ForecastPage'),
  contractorPayments: () => import('../pages/contractor/PaymentsPage'),

  // Branch routes
  branchDashboard: () => import('../pages/branch/DashboardPage'),
  branchPayments: () => import('../pages/branch/PaymentsPage'),

  // Account Manager routes
  amDashboard: () => import('../pages/account-manager/DashboardPage'),
};
